import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthRoutePath } from '../../enums/auth-routes.enum';
import { CurrentUserService } from '../services/auth/current-user.service';
import { rolePermissions } from '../../shared/config/role-permission';
import { UserType } from '../../enums/user-type.enum';

export const permissionGuard: CanActivateFn = (route, state) => {
  const currentUser = inject(CurrentUserService);
  const router = inject(Router);

  const permission = route.data['permission'];
  const requires = route.data['requires'] as
    | { isManager?: boolean; isHomeCare?: boolean }
    | undefined;
  const roles = currentUser.GetUserRoles();
  const user = currentUser.GetSession();

  if (user?.token && currentUser.requiresTwoFactorSetup(user)) {
    router.navigateByUrl(AuthRoutePath.TwoFactorSetup);
    return false;
  }

  if (!roles || roles.length === 0) {
    router.navigateByUrl(AuthRoutePath.Login);
    return false;
  }

  if (checkSpecialRequirements(requires, currentUser)) {
    return true;
  }

  const allowedPermissions: string[] = roles.flatMap((role) => {
    const perms = rolePermissions.get(role);
    return perms ?? [];
  });

  if (!allowedPermissions.includes(permission)) {
    redirectFallback(router, allowedPermissions, user);
    return false;
  }

  return true;
};

function redirectFallback(
  router: Router,
  allowedPermissions: string[],
  user: any
) {
  if (allowedPermissions.includes('DASHBOARD')) {
    router.navigateByUrl('/main/dashboard');
  } else if (user?.patientId && user?.userType === UserType.External) {
    router.navigate(['/main/patient-detail', user.patientId], {
      queryParams: { patientId: user.patientId },
    });
  } else {
    router.navigateByUrl('/main/patient-list');
  }
}

function checkSpecialRequirements(
  requires: any,
  auth: CurrentUserService
): boolean {
  if (!requires) return false;
  return (
    (requires.isManager && auth.isManager) ||
    (requires.isHomeCare && auth.isHomecare)
  );
}
